import { Box, CheckBox, DropButton, Tip } from 'grommet';
import { FormDown, FormUp } from 'grommet-icons';
import { useEffect, useState } from 'preact/hooks';

import TipContent from '@/components/Tooltip';

import { FormulaHighlightType } from './types';

const FORMULA_OPTIONS: { value: FormulaHighlightType; label: string; description: string; group: 'default' | 'test' }[] = [
  { value: 'human', label: 'Human', description: 'Manually set by admin', group: 'default' },
  { value: 'algorithm', label: 'Algorithm', description: 'Set nightly by cron job', group: 'default' },
  { value: 'high_comments', label: 'High Comments', description: 'Comments > 3', group: 'test' },
  { value: 'high_views', label: 'High Views', description: 'Views > 40', group: 'test' },
  { value: 'high_likes', label: 'High Likes', description: 'Likes > 3', group: 'test' },
  {
    value: 'combo_social',
    label: 'Combo Social',
    description: 'Comments > 2 AND Likes > 2 AND reactions from ≥ 2 users',
    group: 'test',
  },
  {
    value: 'top_viral',
    label: 'Top Viral',
    description: 'Views > 20 AND (Likes > 2 OR reactions > 2 users) AND Comments > 1',
    group: 'test',
  },
  { value: 'high_reactions', label: 'High Reactions', description: 'Reactions from ≥ 3 distinct users', group: 'test' },
  {
    value: 'high_likes_or_reactions',
    label: 'High Likes or Reactions',
    description: 'Likes + distinct reacting users ≥ 4',
    group: 'test',
  },
];

interface FormulaFilterPanelProps {
  selectedFormulas: FormulaHighlightType[];
  onFormulaChange: (formulas: FormulaHighlightType[]) => void;
}

const FormulaFilterPanel = ({ selectedFormulas, onFormulaChange }: FormulaFilterPanelProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open]);

  const toggleFormula = (formula: FormulaHighlightType) => {
    const next = selectedFormulas.includes(formula)
      ? selectedFormulas.filter((f) => f !== formula)
      : [...selectedFormulas, formula];
    onFormulaChange(next);
  };

  return (
    <DropButton
      open={open}
      dropAlign={{ top: 'bottom' }}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      dropContent={
        <Box
          background="white"
          round="xsmall"
          pad="small"
          style={{ boxShadow: '0 4px 15px rgba(0, 0, 0, .25)', minWidth: '220px', width: 'max-content' }}
        >
          {FORMULA_OPTIONS.filter((o) => o.group === 'default').map(({ value, label, description }) => (
            <Tip key={value} content={<TipContent message={description} />} dropProps={{ align: { left: 'right' } }} plain>
              <Box pad={{ vertical: 'xxsmall' }}>
                <CheckBox checked={selectedFormulas.includes(value)} label={label} onChange={() => toggleFormula(value)} />
              </Box>
            </Tip>
          ))}
          <hr className="my-1 border-grey-5" />
          {FORMULA_OPTIONS.filter((o) => o.group === 'test').map(({ value, label, description }) => (
            <Tip key={value} content={<TipContent message={description} />} dropProps={{ align: { left: 'right' } }} plain>
              <Box pad={{ vertical: 'xxsmall' }}>
                <CheckBox checked={selectedFormulas.includes(value)} label={label} onChange={() => toggleFormula(value)} />
              </Box>
            </Tip>
          ))}
        </Box>
      }
      dropProps={{ elevation: 'xlarge' }}
    >
      <div className="flex items-center gap-1 rounded-lg bg-primary-green bg-opacity-15 px-2 py-1 text-xs/none text-primary-green hover:bg-opacity-100 hover:text-white">
        <span>Formula Filters{selectedFormulas.length > 0 ? ` (${selectedFormulas.length})` : ''}</span>
        {open ? <FormUp size="16px" /> : <FormDown size="16px" />}
      </div>
    </DropButton>
  );
};

export default FormulaFilterPanel;
